import { Component, OnInit, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SiteActions } from '@next/core-lib/site';
import { Store, select } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { NutrientProperties, SiteState } from '../shared/models';
import {
  GetNutrientProperties,
  GetSoilTypes,
  GetUnitsOfMeasure,
  DeleteNutrientProperties,
  OpenCreateNutrientPropertyDialog,
  GetNutrientDesignations,
  GetNutrientSources,
  OpenEditNutrientPropertyDialog,
} from '../store/actions';
import { selectNutrientProperties } from '../store/selectors';

enum NutrientPropertyAction {
  edit = 'edit',
  remove = 'remove',
}

@Component({
  selector: 'app-nutrient-properties',
  templateUrl: './nutrient-properties.component.html',
  styleUrls: ['./nutrient-properties.component.scss'],
  standalone: false,
})
export class NutrientPropertiesComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);

  private readonly subscription = new Subscription();
  title = this.locale.translate('nutrientProperties.title');
  displayedColumns: string[] = [
    'source',
    'nutrientDesignation',
    'soilType',
    'sampleNumber',
    'soilTargetLow',
    'soilTargetHigh',
    'soilTargetUoM',
    'plantTargetLow',
    'plantTargetHigh',
    'plantTargetUoM',
    'actions',
  ];
  nutrientPropertyActions = [
    {
      icon: 'edit',
      viewValue: this.locale.translate('edit'),
      key: NutrientPropertyAction.edit,
    },
    {
      icon: 'delete',
      viewValue: this.locale.translate('remove'),
      key: NutrientPropertyAction.remove,
    },
  ];
  dataSource = new MatTableDataSource<NutrientProperties>();
  dataAvailable = false;
  isMobile$ = this.breakpoint.ltLg$;

  ngOnInit(): void {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: this.title }));
    this.store.dispatch(new GetNutrientProperties());
    this.store.dispatch(new GetSoilTypes());
    this.store.dispatch(new GetUnitsOfMeasure());
    this.store.dispatch(new GetNutrientDesignations());
    this.store.dispatch(new GetNutrientSources());

    const selectNutrientPropertiesSubscription = this.store
      .pipe(select(selectNutrientProperties))
      .subscribe((result) => {
        this.dataSource.data = result;
        this.dataAvailable = result.length > 0;
        this.cdr.detectChanges();
      });

    this.subscription.add(selectNutrientPropertiesSubscription);

    this.updateData();
  }

  updateData() {
    this.store.dispatch(new GetNutrientProperties());
  }

  onPage() {
    this.updateData();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  createNutrientProperty() {
    this.store.dispatch(new OpenCreateNutrientPropertyDialog());
  }

  nutrientPropertyAction(key: string, id: number) {
    switch (key) {
      case NutrientPropertyAction.edit:
        this.editNutrientProperty(id);
        break;
      case NutrientPropertyAction.remove:
        this.removeNutrientProperty(id);
        break;
    }
  }

  editNutrientProperty(id: number) {
    const nutrientProperty = this.dataSource.data.find((np) => np.id === id);
    this.store.dispatch(new OpenEditNutrientPropertyDialog({ nutrientProperty }));
  }

  removeNutrientProperty(id: number) {
    this.store.dispatch(new DeleteNutrientProperties(id));
  }
}
